import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import StoryCard from '@/pages/weddingStories/components/StoryCard'
import { weddingStories } from '@/mocks/weddingStories'

interface RelatedStoriesSectionProps {
  currentSlug: string
}

export default function RelatedStoriesSection({ currentSlug }: RelatedStoriesSectionProps) {
  const { t } = useTranslation()
  const scroller = useRef<HTMLDivElement>(null)

  const related = weddingStories.filter((story) => story.slug !== currentSlug)

  if (related.length === 0) return null

  const scrollBy = (direction: number) => {
    const el = scroller.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: 'smooth' })
  }

  return (
    <section id="more-stories" className="w-full bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-8 mb-10 md:mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t('weddingStories.related.label')}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t('weddingStories.related.title')}
            </h2>
            <p className="mt-5 text-base text-foreground-600 leading-relaxed">{t('weddingStories.related.intro')}</p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              aria-label={t('weddingStories.related.prev')}
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center border border-background-300 text-foreground-700 cursor-pointer hover:border-primary-400 hover:text-primary-700 hover:bg-background-50 transition-colors"
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>
            <button
              type="button"
              aria-label={t('weddingStories.related.next')}
              onClick={() => scrollBy(1)}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center border border-background-300 text-foreground-700 cursor-pointer hover:border-primary-400 hover:text-primary-700 hover:bg-background-50 transition-colors"
            >
              <i className="ri-arrow-right-line text-xl" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scroller}
        className="no-scrollbar flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory px-4 md:px-8 lg:px-12 pb-2"
      >
        {related.map((story) => (
          <div key={story.id} className="snap-start shrink-0 w-[280px] sm:w-[320px] lg:w-[360px]">
            <StoryCard story={story} />
          </div>
        ))}
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 mt-10">
        <Link
          to="/wedding-stories"
          className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md border border-background-300 text-foreground-800 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-700 transition-colors"
        >
          <i className="ri-book-open-line text-lg" />
          {t('weddingStories.viewAll')}
        </Link>
      </div>
    </section>
  )
}